
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LoadingState, EmptyState, PageHeader } from "@/components/features/states";
import {
  Package,
  Plus,
  Truck,
  Search,
  ShieldCheck,
  TrendingUp,
  Clock,
  CheckCircle2,
  ArrowRight,
  ShoppingBag,
  Megaphone,
  ExternalLink,
  Store as StoreIcon,
  MapPin,
  Wallet as WalletIcon,
  Crown,
  Ticket,
  Navigation,
  ChevronRight,
  CircleDot,
  Droplets,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type {
  Advertisement,
  DeliveryJob,
  JobMode,
  JobStatus,
  ServiceStatus,
  Subscription,
  Wallet,
} from "@/lib/types";
import { FEATURE_FLAGS } from "@/lib/feature-flags";
import {
  currencySymbol,
  formatPrice,
  formatRelativeTime,
} from "@/lib/format";

// ============================================================
// Constants
// ============================================================

/** Active job statuses for the live tracker at the top of the page. */
const ACTIVE_STATUSES: JobStatus[] = ["assigned", "picked_up", "in_transit"];

/** Historical (terminal) job statuses shown in the order history list. */
const HISTORY_STATUSES: JobStatus[] = ["delivered", "completed", "cancelled"];

/** Estimated delivery duration in minutes (used for the ETA countdown). */
const ESTIMATED_DELIVERY_MIN = 30;

const FREQUENT_STOPS_KEY = "horndeliver-frequent-stops";

interface FrequentStop {
  label: string;
  address: string;
}

const DEFAULT_STOPS: FrequentStop[] = [
  { label: "Home", address: "123 Main St" },
  { label: "Office", address: "456 Business Ave" },
  { label: "Gym", address: "789 Fitness Rd" },
];

// ============================================================
// Sponsored ad (kept subtle, at the bottom of the page)
// ============================================================

function DashboardAd({ ad }: { ad: Advertisement }) {
  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    api.ads.track({ id: ad.id, event: "impression" }).catch(() => {});
  }, [ad.id]);

  const onClick = () => {
    api.ads.track({ id: ad.id, event: "click" }).catch(() => {});
    if (ad.targetUrl) {
      window.open(ad.targetUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex shrink-0 items-center gap-2 rounded-xl border border-amber-300/70 bg-amber-50/60 p-3 text-left transition hover:border-amber-400 dark:border-amber-700/60 dark:bg-amber-950/20"
      style={{ minWidth: 240 }}
      aria-label={`Sponsored: ${ad.title}`}
    >
      {ad.imageUrl && (
        <img
          src={ad.imageUrl}
          alt=""
          className="h-12 w-12 shrink-0 rounded-lg object-cover"
          loading="lazy"
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <Badge className="bg-amber-200 px-1.5 py-0 text-[10px] text-amber-800 hover:bg-amber-200 dark:bg-amber-900/60 dark:text-amber-200">
            <Megaphone className="mr-0.5 h-2.5 w-2.5" /> Sponsored
          </Badge>
        </div>
        <p className="mt-0.5 line-clamp-1 text-xs font-semibold">{ad.title}</p>
        {ad.ctaLabel && (
          <span className="mt-0.5 inline-flex items-center text-[10px] font-medium text-amber-700 dark:text-amber-300">
            {ad.ctaLabel} <ExternalLink className="ml-0.5 h-2.5 w-2.5" />
          </span>
        )}
      </div>
    </button>
  );
}

// ============================================================
// Widget 1 — Active Order Tracker
// ============================================================

/** Compute the ETA countdown string for an in-progress delivery. */
function computeEta(job: DeliveryJob): { label: string; tone: "live" | "soon" } {
  const base = job.pickedUpAt ?? job.assignedAt;
  if (!base) return { label: "Preparing", tone: "soon" };
  const target = new Date(base).getTime() + ESTIMATED_DELIVERY_MIN * 60_000;
  const remaining = Math.round((target - Date.now()) / 60_000);
  if (remaining <= 0) return { label: "Arriving soon", tone: "soon" };
  return { label: `Arriving in ~${remaining} min`, tone: "live" };
}

function LiveMapMini({ job }: { job: DeliveryJob }) {
  // Courier position dot — pulsing animation positioned at the midpoint
  // of the route. Stylized (no real GPS available client-side).
  const eta = computeEta(job);
  return (
    <div
      role="img"
      aria-label={`Live map showing courier between pickup and delivery. ${eta.label}.`}
      className="relative h-32 w-full overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/20"
    >
      {/* Faux street grid */}
      <div className="absolute inset-0 opacity-40 dark:opacity-25">
        <div className="absolute left-0 right-0 top-1/3 h-px bg-emerald-200 dark:bg-emerald-800/60" />
        <div className="absolute left-0 right-0 top-2/3 h-px bg-emerald-200 dark:bg-emerald-800/60" />
        <div className="absolute bottom-0 top-0 left-1/4 w-px bg-emerald-200 dark:bg-emerald-800/60" />
        <div className="absolute bottom-0 top-0 left-3/4 w-px bg-emerald-200 dark:bg-emerald-800/60" />
      </div>

      {/* Dashed route from pickup (left) to delivery (right) */}
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M 12 70 Q 50 20 88 70"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeDasharray="3 3"
          className="text-primary/60"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* Pickup pin (orange) */}
      <div className="absolute left-[8%] top-[58%] flex flex-col items-center">
        <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-orange-500 text-[10px] font-bold text-white shadow-md dark:border-emerald-950">
          A
        </span>
      </div>

      {/* Delivery pin (emerald) */}
      <div className="absolute right-[8%] top-[58%] flex flex-col items-center">
        <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-primary text-[10px] font-bold text-primary-foreground shadow-md dark:border-emerald-950">
          B
        </span>
      </div>

      {/* Courier pulsing dot at the midpoint */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <span className="relative flex h-4 w-4">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60" />
          <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-primary shadow-md dark:border-emerald-950">
            <Truck className="h-2.5 w-2.5 text-primary-foreground" />
          </span>
        </span>
      </div>

      {/* ETA chip */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
        <Badge
          className={
            eta.tone === "live"
              ? "border-primary/30 bg-white/90 text-primary dark:bg-emerald-950/80"
              : "border-amber-300 bg-white/90 text-amber-700 dark:bg-amber-950/80 dark:text-amber-300"
          }
        >
          <Clock className="mr-1 h-3 w-3" /> {eta.label}
        </Badge>
      </div>
    </div>
  );
}

function ActiveOrderTracker({
  jobs,
  isLoading,
  navigate,
}: {
  jobs: DeliveryJob[];
  isLoading: boolean;
  navigate: (path: string) => void;
}) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <Card className="border-primary/30">
        <CardContent className="p-4">
          <LoadingState label="Loading active order..." />
        </CardContent>
      </Card>
    );
  }

  if (jobs.length === 0) {
    return (
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="p-4 sm:p-6">
          <EmptyState
            icon={<Truck className="h-10 w-10" />}
            title={t("dashboard.noActiveDeliveries")}
            description="You have no parcels in transit right now. Start a new delivery and track it live here."
            action={
              <Button onClick={() => navigate("app/jobs/new")} className="bg-primary text-primary-foreground">
                <Plus className="mr-1 h-4 w-4" /> Create new delivery
              </Button>
            }
          />
        </CardContent>
      </Card>
    );
  }

  // Show the most recent active job (by assignedAt, then createdAt).
  const job =
    [...jobs].sort((a, b) => {
      const ax = new Date(a.assignedAt ?? a.createdAt).getTime();
      const bx = new Date(b.assignedAt ?? b.createdAt).getTime();
      return bx - ax;
    })[0];

  const providerName = job.provider?.name?.trim();
  const initial = providerName ? providerName.charAt(0).toUpperCase() : null;
  const eta = computeEta(job);

  const statusLabel: Record<JobStatus, string> = {
    open: "Open",
    negotiating: "Negotiating",
    assigned: "Courier assigned",
    picked_up: "Picked up",
    in_transit: "In transit",
    delivered: "Delivered",
    completed: "Completed",
    cancelled: "Cancelled",
    disputed: "Disputed",
  };

  return (
    <Card className="overflow-hidden border-primary/30">
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Navigation className="h-4 w-4" />
          </span>
          <div>
            <CardTitle className="text-base sm:text-lg">Active Order</CardTitle>
            <p className="text-xs text-muted-foreground">
              {job.code} • {statusLabel[job.status]}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0"
          onClick={() => navigate(`app/jobs/${job.id}`)}
        >
          Track <ChevronRight className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-0 sm:p-6 sm:pt-0">
        {/* Address flow: pickup → courier → delivery */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Pickup */}
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-400">
              <CircleDot className="h-3 w-3" /> Pickup
            </p>
            <p className="mt-0.5 line-clamp-2 text-xs font-medium sm:text-sm">
              {job.pickupAddress || "—"}
            </p>
          </div>

          {/* Courier avatar */}
          <div className="flex shrink-0 flex-col items-center">
            {initial ? (
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground shadow-sm">
                {initial}
              </span>
            ) : (
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground shadow-sm">
                <Truck className="h-5 w-5" />
              </span>
            )}
            <span className="mt-0.5 hidden text-[10px] text-muted-foreground sm:block">
              {providerName ?? "Courier"}
            </span>
          </div>

          {/* Delivery */}
          <div className="min-w-0 flex-1 text-right">
            <p className="flex items-center justify-end gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary">
              Delivery <MapPin className="h-3 w-3" />
            </p>
            <p className="mt-0.5 line-clamp-2 text-xs font-medium sm:text-sm">
              {job.dropoffAddress || "—"}
            </p>
          </div>
        </div>

        {/* Live map mini-window */}
        <LiveMapMini job={job} />

        {/* ETA + courier detail row */}
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            <span className={eta.tone === "live" ? "font-semibold text-primary" : "font-semibold text-amber-600 dark:text-amber-400"}>
              {eta.label}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-7 border-primary/30 text-xs"
            onClick={() => navigate(`app/jobs/${job.id}`)}
          >
            View details <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Widget 2 — Quick Actions
// ============================================================

function QuickActions({ navigate }: { navigate: (path: string) => void }) {
  const { t } = useTranslation();

  // Launch the dedicated mobile-first Water Order form with a preselected
  // provider (ahla | zamzam). The form enforces MOQ=5 per product.
  const startWaterOrder = (provider: "ahla" | "zamzam") => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("waterProvider", provider);
    }
    navigate("app/water-order");
  };

  return (
    <div className="space-y-3">
      {/* Pickup & Deliver — primary action, full width */}
      <button
        type="button"
        onClick={() => navigate("app/pickup-deliver")}
        className="group flex h-20 w-full items-center gap-3 rounded-xl bg-primary px-4 text-primary-foreground shadow-sm transition hover:shadow-md active:scale-[0.99] sm:h-24 sm:px-5"
        aria-label="New pickup and deliver job"
      >
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-foreground/15">
          <Package className="h-6 w-6" />
        </span>
        <span className="flex flex-col items-start text-left">
          <span className="text-base font-bold leading-tight sm:text-lg">+ New Pickup &amp; Deliver</span>
          <span className="text-xs text-primary-foreground/80">
            Send a parcel or run an errand
          </span>
        </span>
      </button>

      {/* Water marketplace — the only orderable merchants at launch */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* Bottled Water — Ahla */}
        <Card
          className="group cursor-pointer overflow-hidden border-primary/30 transition hover:border-primary/60 hover:shadow-md"
          onClick={() => startWaterOrder("ahla")}
        >
          <div className="relative aspect-[4/3] w-full overflow-hidden bg-gradient-to-b from-sky-50 to-primary/5">
            <div className="flex h-full w-full items-center justify-center p-6">
              <img
                src="/water/ahla-logo.png"
                alt={t("dashboard.bottledWater")}
                className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <Badge className="absolute left-2 top-2 bg-primary/95 text-primary-foreground">
              <Droplets className="mr-1 h-3 w-3" /> Bottled
            </Badge>
          </div>
          <CardContent className="p-3">
            <p className="font-semibold">{t("dashboard.bottledWater")}</p>
            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
              {t("dashboard.bottledWaterHint")}
            </p>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs font-medium text-primary">
                {t("dashboard.bottledWaterCta")}
              </span>
              <ChevronRight className="h-4 w-4 text-primary" />
            </div>
          </CardContent>
        </Card>

        {/* Refilled Water — ZamZam Water */}
        <Card
          className="group cursor-pointer overflow-hidden border-primary/30 transition hover:border-primary/60 hover:shadow-md"
          onClick={() => startWaterOrder("zamzam")}
        >
          <div className="relative aspect-[4/3] w-full overflow-hidden bg-gradient-to-b from-emerald-50 to-primary/5">
            <div className="flex h-full w-full items-center justify-center p-6">
              <img
                src="/water/zamzam-logo.png"
                alt={t("dashboard.refilledWater")}
                className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <Badge className="absolute left-2 top-2 bg-emerald-600/95 text-white">
              <Droplets className="mr-1 h-3 w-3" /> Refill
            </Badge>
          </div>
          <CardContent className="p-3">
            <p className="font-semibold">{t("dashboard.refilledWater")}</p>
            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
              {t("dashboard.refilledWaterHint")}
            </p>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs font-medium text-primary">
                {t("dashboard.refilledWaterCta")}
              </span>
              <ChevronRight className="h-4 w-4 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Coming Soon — placeholder for future merchants */}
      <Card className="border-dashed border-border bg-muted/30">
        <CardContent className="flex flex-col items-center justify-center gap-2 p-6 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <StoreIcon className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-semibold">{t("dashboard.comingSoon")}</p>
          <p className="max-w-xs text-xs text-muted-foreground">
            {t("dashboard.comingSoonDesc")}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// Widget 3 — Frequent Stops
// ============================================================

function loadStops(): FrequentStop[] {
  if (typeof window === "undefined") return DEFAULT_STOPS;
  try {
    const raw = window.localStorage.getItem(FREQUENT_STOPS_KEY);
    if (!raw) {
      window.localStorage.setItem(FREQUENT_STOPS_KEY, JSON.stringify(DEFAULT_STOPS));
      return DEFAULT_STOPS;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return DEFAULT_STOPS;
    const cleaned = parsed
      .filter((s): s is FrequentStop =>
        !!s && typeof s === "object" && typeof (s as FrequentStop).label === "string" && typeof (s as FrequentStop).address === "string"
      )
      .map((s) => ({ label: s.label, address: s.address }));
    return cleaned.length ? cleaned : DEFAULT_STOPS;
  } catch {
    return DEFAULT_STOPS;
  }
}

function saveStops(stops: FrequentStop[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(FREQUENT_STOPS_KEY, JSON.stringify(stops));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

function FrequentStops({ navigate }: { navigate: (path: string) => void }) {
  const [stops, setStops] = useState<FrequentStop[]>(() => loadStops());

  const addStop = () => {
    const label = window.prompt("Name this stop (e.g. Home, Office, Gym)");
    if (!label || !label.trim()) return;
    const address = window.prompt("Enter the address");
    if (!address || !address.trim()) return;
    const next = [...stops, { label: label.trim(), address: address.trim() }];
    setStops(next);
    saveStops(next);
  };

  const removeStop = (idx: number) => {
    const next = stops.filter((_, i) => i !== idx);
    setStops(next);
    saveStops(next);
  };

  return (
    <section aria-labelledby="frequent-stops-heading">
      <div className="mb-2 flex items-center justify-between">
        <h2 id="frequent-stops-heading" className="text-sm font-semibold">
          Frequent Stops
        </h2>
        <span className="text-[11px] text-muted-foreground">Tap to start a delivery</span>
      </div>
      <div className="scroll-area -mx-3 flex snap-x gap-2 overflow-x-auto px-3 pb-2">
        {stops.map((stop, idx) => (
          <button
            key={`${stop.label}-${idx}`}
            type="button"
            onClick={() => navigate("app/jobs/new")}
            onContextMenu={(e) => {
              e.preventDefault();
              removeStop(idx);
            }}
            title={`${stop.label} — ${stop.address} (right-click to remove)`}
            className="flex w-40 shrink-0 snap-start items-center gap-2 rounded-xl border border-border bg-card p-3 text-left transition hover:border-primary/40 hover:shadow-sm active:scale-95"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <MapPin className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-semibold">{stop.label}</span>
              <span className="block truncate text-[11px] text-muted-foreground">
                {stop.address}
              </span>
            </span>
          </button>
        ))}

        <button
          type="button"
          onClick={addStop}
          className="flex w-32 shrink-0 snap-start items-center justify-center gap-1.5 rounded-xl border border-dashed border-border bg-card/50 p-3 text-xs font-medium text-muted-foreground transition hover:border-primary/40 hover:text-primary active:scale-95"
          aria-label="Add a new frequent stop"
        >
          <Plus className="h-4 w-4" /> Add stop
        </button>
      </div>
    </section>
  );
}

// ============================================================
// Widget 4 — Order History
// ============================================================

type HistoryFilter = "all" | "picked_up" | "delivered";

const HISTORY_FILTERS: { key: HistoryFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "picked_up", label: "Picked Up" },
  { key: "delivered", label: "Delivered" },
];

function statusBadgeClasses(status: JobStatus): string {
  if (status === "delivered" || status === "completed") {
    return "border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300";
  }
  if (status === "cancelled") {
    return "border-red-300 bg-red-100 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300";
  }
  if (ACTIVE_STATUSES.includes(status)) {
    return "border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300";
  }
  return "border-border bg-muted text-muted-foreground";
}

function modeBadgeClasses(mode: JobMode): string {
  if (mode === "order_deliver") {
    return "border-gold/40 bg-gold/10 text-gold";
  }
  return "border-primary/30 bg-primary/10 text-primary";
}

function JobHistoryRow({
  job,
  navigate,
}: {
  job: DeliveryJob;
  navigate: (path: string) => void;
}) {
  const statusLabel: Record<JobStatus, string> = {
    open: "Open",
    negotiating: "Negotiating",
    assigned: "Assigned",
    picked_up: "Picked up",
    in_transit: "In transit",
    delivered: "Delivered",
    completed: "Completed",
    cancelled: "Cancelled",
    disputed: "Disputed",
  };
  const price = job.agreedPrice ?? job.customerOffer;
  return (
    <button
      type="button"
      onClick={() => navigate(`app/jobs/${job.id}`)}
      className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition hover:border-primary/40 hover:shadow-sm active:scale-[0.99]"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        {job.mode === "order_deliver" ? (
          <ShoppingBag className="h-5 w-5" />
        ) : (
          <Package className="h-5 w-5" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-muted-foreground">
            #{job.code}
          </span>
          <Badge variant="outline" className={`px-1.5 py-0 text-[10px] ${modeBadgeClasses(job.mode)}`}>
            {job.mode === "order_deliver" ? "Store Order" : "Pickup & Deliver"}
          </Badge>
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          <span className="truncate">{job.pickupAddress || "—"}</span>
          <ArrowRight className="mx-1 inline h-3 w-3" />
          <span className="truncate">{job.dropoffAddress || "—"}</span>
        </p>
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>{formatRelativeTime(job.createdAt)}</span>
          {job.store?.name && (
            <span className="truncate">• {job.store.name}</span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-sm font-bold text-gold">
          {formatPrice(price)}
        </span>
        <Badge variant="outline" className={`px-1.5 py-0 text-[10px] ${statusBadgeClasses(job.status)}`}>
          {statusLabel[job.status]}
        </Badge>
      </div>
    </button>
  );
}

function OrderHistory({
  jobs,
  isLoading,
  navigate,
}: {
  jobs: DeliveryJob[];
  isLoading: boolean;
  navigate: (path: string) => void;
}) {
  const [filter, setFilter] = useState<HistoryFilter>("all");

  // Terminal-state jobs only (active ones live in the tracker).
  const historyJobs = useMemo(
    () =>
      jobs
        .filter((j) => HISTORY_STATUSES.includes(j.status))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [jobs]
  );

  const filtered = useMemo(() => {
    if (filter === "picked_up") {
      return historyJobs.filter((j) => !!j.pickedUpAt);
    }
    if (filter === "delivered") {
      return historyJobs.filter(
        (j) => j.status === "delivered" || j.status === "completed" || !!j.deliveredAt
      );
    }
    return historyJobs;
  }, [historyJobs, filter]);

  return (
    <section aria-labelledby="order-history-heading">
      <div className="mb-2 flex items-center justify-between">
        <h2 id="order-history-heading" className="text-sm font-semibold">
          Order History
        </h2>
        <Button variant="ghost" size="sm" onClick={() => navigate("app/jobs")}>
          View all <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Filter pills */}
      <div className="mb-3 flex gap-1.5" role="tablist" aria-label="Filter order history">
        {HISTORY_FILTERS.map((f) => (
          <button
            key={f.key}
            role="tab"
            aria-selected={filter === f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition active:scale-95 ${
              filter === f.key
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <LoadingState label="Loading your orders..." />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Package className="h-8 w-8" />}
          title="No orders yet"
          description="Your past deliveries will appear here once completed."
          action={
            <Button size="sm" onClick={() => navigate("app/jobs/new")}>
              <Plus className="mr-1 h-4 w-4" /> New delivery
            </Button>
          }
        />
      ) : (
        <div className="space-y-2">
          {filtered.slice(0, 20).map((job) => (
            <JobHistoryRow key={job.id} job={job} navigate={navigate} />
          ))}
        </div>
      )}
    </section>
  );
}

// ============================================================
// Widget 5 — Wallet & Subscription
// ============================================================

function WalletAndSubscription({
  wallets,
  subscription,
  isLoading,
  navigate,
  userCountryCode,
}: {
  wallets: Wallet[];
  subscription: Subscription | null;
  isLoading: boolean;
  navigate: (path: string) => void;
  userCountryCode: string;
}) {
  const senderWallet = wallets.find((w) => w.type === "sender") ?? null;
  const symbol = currencySymbol(userCountryCode);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <LoadingState label="Loading wallet..." />
        </CardContent>
      </Card>
    );
  }

  const subActive = subscription && subscription.status === "active";

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {/* Wallet balance */}
      <Card className="border-primary/20">
        <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
          <CardTitle className="flex items-center gap-1.5 text-sm font-semibold">
            <WalletIcon className="h-4 w-4 text-primary" /> Wallet
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => navigate("app/wallet")}
          >
            Top up <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {senderWallet ? (
            <>
              <p className="text-2xl font-bold text-gold">
                {new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(senderWallet.balance)} {symbol}
              </p>
              {senderWallet.pendingBalance > 0 && (
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  Pending: {senderWallet.pendingBalance} {symbol}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No sender wallet yet.</p>
          )}

          {/* Promo line */}
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-primary/5 p-2 text-xs">
            <Ticket className="h-4 w-4 shrink-0 text-primary" />
            <span className="text-muted-foreground">
              {subActive
                ? `${subscription?.plan?.commissionDiscount ?? 0}% off delivery fees with your Pass`
                : "2 FREE deliveries on your first week"}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Subscription */}
      <Card className={subActive ? "border-gold/40 bg-gold/5" : "border-border"}>
        <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
          <CardTitle className="flex items-center gap-1.5 text-sm font-semibold">
            <Crown className="h-4 w-4 text-gold" /> HornDeliver Pass
          </CardTitle>
          {subActive && (
            <Badge className="border-transparent bg-emerald-500 text-[10px] text-white hover:bg-emerald-500">
              Active
            </Badge>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {subActive ? (
            <>
              <p className="text-base font-bold">{subscription?.plan?.name ?? "HornDeliver Pass"}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {subscription?.endsAt
                  ? `Renews ${formatRelativeTime(subscription.endsAt)}`
                  : "Active subscription"}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 h-7 px-2 text-xs"
                onClick={() => navigate("app/subscriptions")}
              >
                Manage <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm font-medium">Unlock free deliveries</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Save on every order with the HornDeliver Pass.
              </p>
              <Button
                size="sm"
                className="mt-2 h-8 border-gold bg-gold/10 text-gold hover:bg-gold/20"
                onClick={() => navigate("app/subscriptions")}
              >
                <Crown className="mr-1 h-3.5 w-3.5" /> Upgrade to Pass
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// Main — AppDashboard
// ============================================================

export function AppDashboard() {
  const { user } = useAuthStore();
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();
  const role = user?.activeRole;

  const isDeliverer = role === "driver" || role === "provider";
  const isMerchant = role === "merchant";

  // Customer jobs (for active tracker + history). Polled every 30s for "live" feel.
  const { data: custJobsData, isLoading: custJobsLoading } = useQuery({
    queryKey: ["jobs", "customer", "dashboard"],
    queryFn: () => api.jobs.list({ role: "customer" }),
    enabled: !isDeliverer && !isMerchant && !!user,
    refetchInterval: 30_000,
  });
  const customerJobs: DeliveryJob[] = custJobsData?.data?.jobs ?? [];

  // Deliverer jobs (used by the unchanged deliverer branch).
  const { data: delJobsData, isLoading: delJobsLoading } = useQuery({
    queryKey: ["jobs", "dashboard", role],
    queryFn: () => api.jobs.list({ role: "provider" }),
    enabled: isDeliverer && !!user,
  });
  const delivererJobs: DeliveryJob[] = delJobsData?.data?.jobs ?? [];

  // Wallet + subscription (customer only).
  const { data: walletData, isLoading: walletLoading } = useQuery({
    queryKey: ["wallet", "me"],
    queryFn: () => api.wallet.me(),
    enabled: !isDeliverer && !isMerchant && !!user,
    staleTime: 30_000,
  });
  const wallets: Wallet[] = walletData?.data?.wallets ?? [];

  const { data: subData } = useQuery({
    queryKey: ["subscriptions", "mine"],
    queryFn: () => api.subscriptions.mine(),
    enabled: !isDeliverer && !isMerchant && !!user,
    staleTime: 60_000,
  });
  const subscription: Subscription | null = subData?.data?.subscription ?? null;

  // Sponsored ads (subtle, bottom). Gated by ADS_ENABLED feature flag.
  const showAds = FEATURE_FLAGS.ADS_ENABLED && !isDeliverer && !isMerchant;
  const { data: adsData } = useQuery({
    queryKey: ["ads", "feed", "dashboard"],
    queryFn: () => api.ads.feed({}),
    enabled: showAds && !!user,
    staleTime: 60_000,
  });
  const ads: Advertisement[] = adsData?.data?.ads ?? [];

  // Phase 4: service-status gate (used by deliverer branch's vertical awareness).
  const { data: svcData } = useQuery({
    queryKey: ["services", "status"],
    queryFn: () => api.services.status(),
    staleTime: 60_000,
  });
  const services: ServiceStatus[] = svcData?.data?.services ?? [];
  void services; // retained for parity with prior dashboard; not used in customer widgets.

  const userCountryCode = user?.countryCode ?? "+253";

  // --- Merchant dashboard quick-link ---
  if (isMerchant) {
    return (
      <div className="space-y-5">
        <PageHeader
          title={`${t("dashboard.greeting")}, ${user?.name.split(" ")[0]} 👋`}
          subtitle={t("merchant.subtitle")}
        />
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <StoreIcon className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{t("merchant.title")}</p>
              <p className="text-xs text-muted-foreground">{t("merchant.subtitle")}</p>
            </div>
            <Button size="sm" onClick={() => navigate("merchant")}>
              {t("dashboard.merchantDashboard")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </CardContent>
        </Card>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <ActionTile icon={StoreIcon} label={t("merchant.manageMenu")} onClick={() => navigate("merchant")} />
          <ActionTile icon={ShoppingBag} label={t("merchant.orders")} onClick={() => navigate("merchant")} />
          <ActionTile icon={WalletIcon} label={t("merchant.payout")} onClick={() => navigate("merchant")} />
          <ActionTile icon={Package} label={t("dashboard.myJobs")} onClick={() => navigate("app/jobs")} />
        </div>
      </div>
    );
  }

  // --- Deliverer dashboard (driver/provider) ---
  if (isDeliverer) {
    const activeJobs = delivererJobs.filter((j) =>
      ["assigned", "picked_up", "in_transit", "delivered"].includes(j.status)
    );
    const completedJobs = delivererJobs.filter((j) => j.status === "completed");
    return (
      <div className="space-y-5">
        <PageHeader
          title={`${t("dashboard.greeting")}, ${user?.name.split(" ")[0]} 👋`}
          subtitle={
            role === "driver"
              ? t("dashboard.subtitleDriver")
              : t("dashboard.subtitleProvider")
          }
        />
        <div className="grid grid-cols-4 gap-2 sm:gap-3">
          <ActionTile icon={Search} label={t("dashboard.findJobs")} onClick={() => navigate("app/jobs")} />
          <ActionTile icon={Truck} label={t("dashboard.myDeliveries")} onClick={() => navigate("app/jobs")} />
          <ActionTile icon={WalletIcon} label={t("earnings.title")} onClick={() => navigate("app/earnings")} />
          {role === "provider" && (
            <ActionTile
              icon={Plus}
              label={t("dashboard.switchToSender")}
              onClick={() => useAuthStore.getState().switchRole("customer")}
            />
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <StatCard icon={Clock} label={t("dashboard.statActive")} value={activeJobs.length} color="text-amber-600" />
          <StatCard icon={TrendingUp} label={t("dashboard.statAvailable")} value={delivererJobs.length} color="text-primary" />
          <StatCard icon={CheckCircle2} label={t("dashboard.statCompleted")} value={completedJobs.length} color="text-emerald-600" />
        </div>
        <ActiveJobsList
          jobs={activeJobs}
          isLoading={delJobsLoading}
          isDeliverer
          navigate={navigate}
        />
      </div>
    );
  }

  // --- Customer dashboard (5-widget redesign) ---
  const activeJobs = customerJobs.filter((j) => ACTIVE_STATUSES.includes(j.status));

  return (
    <div className="space-y-5">
      <PageHeader title="Dashboard" subtitle="Manage your deliveries" />

      {/* Trust banner — compact on mobile */}
      {user && user.trustStatus !== "verified" && (
        <Card className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30">
          <CardContent className="flex items-center gap-2.5 p-3 sm:gap-3 sm:p-4">
            <ShieldCheck className="h-7 w-7 shrink-0 text-amber-600 sm:h-8 sm:w-8" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                {user.trustStatus === "pending"
                  ? t("dashboard.verifyBannerTitlePending")
                  : t("dashboard.verifyBannerTitle")}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {user.trustStatus === "pending"
                  ? t("dashboard.verifyBannerDescPending")
                  : t("dashboard.verifyBannerDesc")}
              </p>
            </div>
            {user.trustStatus !== "pending" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate("app/trust")}
                className="shrink-0"
              >
                {t("common.verify")}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* 1. Active order tracker */}
      <ActiveOrderTracker
        jobs={activeJobs}
        isLoading={custJobsLoading}
        navigate={navigate}
      />

      {/* 2. Quick actions */}
      <QuickActions navigate={navigate} />

      {/* 3. Frequent stops */}
      <FrequentStops navigate={navigate} />

      {/* 4. Order history */}
      <OrderHistory
        jobs={customerJobs}
        isLoading={custJobsLoading}
        navigate={navigate}
      />

      {/* 5. Wallet & subscription */}
      <WalletAndSubscription
        wallets={wallets}
        subscription={subscription}
        isLoading={walletLoading}
        navigate={navigate}
        userCountryCode={userCountryCode}
      />

      {/* Sponsored strip — subtle, bottom. Hidden until ADS_ENABLED. */}
      {showAds && ads.length > 0 && (
        <section aria-labelledby="promoted-heading">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Megaphone className="h-3.5 w-3.5" /> <span id="promoted-heading">Promoted</span>
          </div>
          <div className="scroll-area -mx-3 flex gap-2 overflow-x-auto px-3 pb-1">
            {ads.slice(0, 4).map((ad) => (
              <DashboardAd key={ad.id} ad={ad} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ============================================================
// Shared helpers for the deliverer branch
// ============================================================

function ActiveJobsList({
  jobs,
  isLoading,
  isDeliverer,
  navigate,
}: {
  jobs: DeliveryJob[];
  isLoading: boolean;
  isDeliverer: boolean;
  navigate: (path: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-semibold">{isDeliverer ? t("dashboard.activeDeliveries") : t("dashboard.activeJobs")}</h2>
        <Button variant="ghost" size="sm" onClick={() => navigate("app/jobs")}>
          {t("common.viewAll")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      </div>
      {isLoading ? (
        <LoadingState />
      ) : jobs.length === 0 ? (
        <EmptyState
          icon={<Package className="h-8 w-8" />}
          title={isDeliverer ? t("dashboard.noActiveDeliveries") : t("dashboard.noActiveJobs")}
          description={
            isDeliverer
              ? t("dashboard.noActiveDeliveriesDesc")
              : t("dashboard.noActiveJobsDesc")
          }
          action={
            <Button size="sm" onClick={() => navigate(isDeliverer ? "app/jobs" : "app/jobs/new")}>
              {isDeliverer ? t("dashboard.findJobs") : t("dashboard.postJob")}
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {jobs.slice(0, 5).map((job) => (
            <DelivererJobRow key={job.id} job={job} navigate={navigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function DelivererJobRow({
  job,
  navigate,
}: {
  job: DeliveryJob;
  navigate: (path: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => navigate(`app/jobs/${job.id}`)}
      className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition hover:border-primary/40 hover:shadow-sm active:scale-[0.99]"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Truck className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">#{job.code}</p>
        <p className="truncate text-xs text-muted-foreground">
          {job.pickupAddress} → {job.dropoffAddress}
        </p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          {formatRelativeTime(job.createdAt)}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-sm font-bold text-gold">
          {formatPrice(job.agreedPrice ?? job.customerOffer)}
        </span>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
    </button>
  );
}

function ActionTile({
  icon: Icon,
  label,
  onClick,
  highlight,
}: {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  highlight?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center gap-2 rounded-xl border p-3 text-center transition hover:shadow-sm active:scale-95 sm:p-4 ${
        highlight
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card hover:border-primary/40"
      }`}
    >
      <Icon className="h-6 w-6" />
      <span className="text-[11px] font-semibold leading-tight sm:text-xs">{label}</span>
    </button>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-1 p-3 sm:p-4">
        <Icon className={`h-5 w-5 ${color}`} />
        <p className="text-xl font-bold sm:text-2xl">{value}</p>
        <p className="text-[11px] text-muted-foreground sm:text-xs">{label}</p>
      </CardContent>
    </Card>
  );
}
